import {
  CareerRoleFullPictureResponse,
  SeniorityProfileFullResponse,
  SkillDependencyRefDto,
  SkillTopicItemDto,
  SkillWithResourcesResponse,
  TopicDependencyRefDto
} from '../../../../models';

export interface RoleDependencyGraphNode {
  id: string;
  label: string;
  kind: 'skill' | 'topic';
  subtitle?: string;
  skillId: string;
  skillName: string;
  topicName?: string;
  topic?: SkillTopicItemDto;
  children: RoleDependencyGraphNode[];
  dependencyLabels: string[];
  unresolvedDependencies: string[];
}

export interface RoleDependencyGraphSection {
  key: string;
  specializationSlug: string | null;
  specializationName: string;
  seniorityLevel: string;
  heading: string;
  skillRoots: RoleDependencyGraphNode[];
  topicRoots: RoleDependencyGraphNode[];
  skillNodeCount: number;
  topicNodeCount: number;
}

interface GraphProfileContext {
  specializationSlug: string | null;
  specializationName: string;
  seniority: SeniorityProfileFullResponse;
}

interface TopicNodeData {
  key: string;
  skillId: string;
  skillName: string;
  topicName: string;
  topic: SkillTopicItemDto;
}

interface GraphMaps<TNode> {
  nodeById: Map<string, TNode>;
  childIdsByParentId: Map<string, string[]>;
  indegreeById: Map<string, number>;
  dependencyLabelsById: Map<string, string[]>;
  unresolvedLabelsById: Map<string, string[]>;
}

export function buildRoleDependencyGraphSections(
  picture: CareerRoleFullPictureResponse | null | undefined
): RoleDependencyGraphSection[] {
  if (!picture) {
    return [];
  }

  const contexts: GraphProfileContext[] = [
    ...picture.seniorityProfiles.map((seniority) => ({
      specializationSlug: null,
      specializationName: 'Base role',
      seniority
    })),
    ...picture.specializations.flatMap((specialization) =>
      specialization.seniorityProfiles.map((seniority) => ({
        specializationSlug: specialization.slug,
        specializationName: specialization.name,
        seniority
      }))
    )
  ];

  return contexts
    .map((context) => buildSection(context))
    .sort((left, right) => left.heading.localeCompare(right.heading));
}

function buildSection(context: GraphProfileContext): RoleDependencyGraphSection {
  const skillMaps = buildSkillMaps(context.seniority.skills ?? []);
  const topicMaps = buildTopicMaps(context.seniority.skills ?? []);
  const specializationKey = context.specializationSlug ?? 'base';
  const seniorityKey = normalizeKey(context.seniority.level);

  return {
    key: `${specializationKey}::${seniorityKey}`,
    specializationSlug: context.specializationSlug,
    specializationName: context.specializationName,
    seniorityLevel: context.seniority.level,
    heading: `${context.specializationName} - ${labelize(context.seniority.level)}`,
    skillRoots: buildRoots(skillMaps, createSkillNode),
    topicRoots: buildRoots(topicMaps, createTopicNode),
    skillNodeCount: skillMaps.nodeById.size,
    topicNodeCount: topicMaps.nodeById.size
  };
}

function buildSkillMaps(skills: SkillWithResourcesResponse[]): GraphMaps<SkillWithResourcesResponse> {
  const nodeById = new Map<string, SkillWithResourcesResponse>();
  const childIdsByParentId = new Map<string, string[]>();
  const indegreeById = new Map<string, number>();
  const dependencyLabelsById = new Map<string, string[]>();
  const unresolvedLabelsById = new Map<string, string[]>();

  for (const skill of skills) {
    const id = skill.skill.id;
    nodeById.set(id, skill);
    childIdsByParentId.set(id, []);
    indegreeById.set(id, 0);
    dependencyLabelsById.set(id, []);
    unresolvedLabelsById.set(id, []);
  }

  for (const skill of skills) {
    const id = skill.skill.id;
    for (const dependency of skill.dependsOn ?? []) {
      connectSkillDependency(
        dependency,
        id,
        nodeById,
        childIdsByParentId,
        indegreeById,
        dependencyLabelsById,
        unresolvedLabelsById
      );
    }
  }

  return {
    nodeById,
    childIdsByParentId,
    indegreeById,
    dependencyLabelsById,
    unresolvedLabelsById
  };
}

function connectSkillDependency(
  dependency: SkillDependencyRefDto,
  targetSkillId: string,
  nodeById: Map<string, SkillWithResourcesResponse>,
  childIdsByParentId: Map<string, string[]>,
  indegreeById: Map<string, number>,
  dependencyLabelsById: Map<string, string[]>,
  unresolvedLabelsById: Map<string, string[]>
): void {
  const dependencyLabels = dependencyLabelsById.get(targetSkillId) ?? [];
  const unresolvedLabels = unresolvedLabelsById.get(targetSkillId) ?? [];
  const sourceSkill = nodeById.get(dependency.skillId);

  if (sourceSkill) {
    dependencyLabels.push(sourceSkill.skill.name);
    childIdsByParentId.get(sourceSkill.skill.id)?.push(targetSkillId);
    indegreeById.set(targetSkillId, (indegreeById.get(targetSkillId) ?? 0) + 1);
  } else {
    unresolvedLabels.push(unresolvedSkillLabel(dependency.skillId));
  }

  dependencyLabelsById.set(targetSkillId, uniqueSorted(dependencyLabels));
  unresolvedLabelsById.set(targetSkillId, uniqueSorted(unresolvedLabels));
}

function buildTopicMaps(skills: SkillWithResourcesResponse[]): GraphMaps<TopicNodeData> {
  const nodeById = new Map<string, TopicNodeData>();
  const childIdsByParentId = new Map<string, string[]>();
  const indegreeById = new Map<string, number>();
  const dependencyLabelsById = new Map<string, string[]>();
  const unresolvedLabelsById = new Map<string, string[]>();
  const skillNameById = new Map<string, string>();

  for (const skill of skills) {
    skillNameById.set(skill.skill.id, skill.skill.name);
    for (const topic of resolveTopicItems(skill)) {
      const key = topicKey(skill.skill.id, topic.name);
      nodeById.set(key, {
        key,
        skillId: skill.skill.id,
        skillName: skill.skill.name,
        topicName: topic.name,
        topic
      });
      childIdsByParentId.set(key, []);
      indegreeById.set(key, 0);
      dependencyLabelsById.set(key, []);
      unresolvedLabelsById.set(key, []);
    }
  }

  for (const topicNode of nodeById.values()) {
    for (const dependency of topicNode.topic.dependsOn ?? []) {
      connectTopicDependency(
        dependency,
        topicNode,
        nodeById,
        childIdsByParentId,
        indegreeById,
        dependencyLabelsById,
        unresolvedLabelsById,
        skillNameById
      );
    }
  }

  return {
    nodeById,
    childIdsByParentId,
    indegreeById,
    dependencyLabelsById,
    unresolvedLabelsById
  };
}

function connectTopicDependency(
  dependency: TopicDependencyRefDto,
  topicNode: TopicNodeData,
  nodeById: Map<string, TopicNodeData>,
  childIdsByParentId: Map<string, string[]>,
  indegreeById: Map<string, number>,
  dependencyLabelsById: Map<string, string[]>,
  unresolvedLabelsById: Map<string, string[]>,
  skillNameById: Map<string, string>
): void {
  const dependencySkillId = dependency.skillId?.trim() || topicNode.skillId;
  const dependencyKey = topicKey(dependencySkillId, dependency.topicName);
  const dependencyLabels = dependencyLabelsById.get(topicNode.key) ?? [];
  const unresolvedLabels = unresolvedLabelsById.get(topicNode.key) ?? [];
  const sourceTopic = nodeById.get(dependencyKey);

  if (sourceTopic) {
    dependencyLabels.push(topicDisplayLabel(sourceTopic.skillName, sourceTopic.topicName));
    childIdsByParentId.get(sourceTopic.key)?.push(topicNode.key);
    indegreeById.set(topicNode.key, (indegreeById.get(topicNode.key) ?? 0) + 1);
  } else {
    const fallbackSkillName = skillNameById.get(dependencySkillId) ?? dependencySkillId;
    unresolvedLabels.push(topicDisplayLabel(fallbackSkillName, dependency.topicName));
  }

  dependencyLabelsById.set(topicNode.key, uniqueSorted(dependencyLabels));
  unresolvedLabelsById.set(topicNode.key, uniqueSorted(unresolvedLabels));
}

function buildRoots<TNode>(
  maps: GraphMaps<TNode>,
  factory: (node: TNode, maps: GraphMaps<TNode>, path: Set<string>) => RoleDependencyGraphNode
): RoleDependencyGraphNode[] {
  const ids = Array.from(maps.nodeById.keys());
  if (!ids.length) {
    return [];
  }

  const rootIds = ids.filter((id) => (maps.indegreeById.get(id) ?? 0) === 0);
  const orderedRootIds = rootIds.length ? rootIds : ids;

  return orderedRootIds
    .sort((left, right) => compareNodeLabels(maps.nodeById.get(left), maps.nodeById.get(right)))
    .map((id) => maps.nodeById.get(id))
    .filter((node): node is TNode => !!node)
    .map((node) => factory(node, maps, new Set<string>()));
}

function createSkillNode(
  skill: SkillWithResourcesResponse,
  maps: GraphMaps<SkillWithResourcesResponse>,
  path: Set<string>
): RoleDependencyGraphNode {
  const id = skill.skill.id;
  const nextPath = new Set(path);
  nextPath.add(id);

  const childIds = uniqueSorted(maps.childIdsByParentId.get(id) ?? []).filter((childId) => !nextPath.has(childId));
  const children = childIds
    .map((childId) => maps.nodeById.get(childId))
    .filter((child): child is SkillWithResourcesResponse => !!child)
    .sort((left, right) => compareSkill(left, right))
    .map((child) => createSkillNode(child, maps, nextPath));

  return {
    id: `skill:${id}`,
    label: skill.skill.name,
    kind: 'skill',
    subtitle: skill.category,
    skillId: id,
    skillName: skill.skill.name,
    children,
    dependencyLabels: maps.dependencyLabelsById.get(id) ?? [],
    unresolvedDependencies: maps.unresolvedLabelsById.get(id) ?? []
  };
}

function createTopicNode(
  topic: TopicNodeData,
  maps: GraphMaps<TopicNodeData>,
  path: Set<string>
): RoleDependencyGraphNode {
  const id = topic.key;
  const nextPath = new Set(path);
  nextPath.add(id);

  const childIds = uniqueSorted(maps.childIdsByParentId.get(id) ?? []).filter((childId) => !nextPath.has(childId));
  const children = childIds
    .map((childId) => maps.nodeById.get(childId))
    .filter((child): child is TopicNodeData => !!child)
    .sort((left, right) => compareTopic(left, right))
    .map((child) => createTopicNode(child, maps, nextPath));

  return {
    id: `topic:${id}`,
    label: topic.topicName,
    kind: 'topic',
    subtitle: topic.skillName,
    skillId: topic.skillId,
    skillName: topic.skillName,
    topicName: topic.topicName,
    topic: topic.topic,
    children,
    dependencyLabels: maps.dependencyLabelsById.get(id) ?? [],
    unresolvedDependencies: maps.unresolvedLabelsById.get(id) ?? []
  };
}

function compareSkill(left: SkillWithResourcesResponse, right: SkillWithResourcesResponse): number {
  return compareSortOrder(left.sortOrder, right.sortOrder) || left.skill.name.localeCompare(right.skill.name);
}

function compareTopic(left: TopicNodeData, right: TopicNodeData): number {
  return (
    compareSortOrder(left.topic.sortOrder ?? undefined, right.topic.sortOrder ?? undefined) ||
    left.topicName.localeCompare(right.topicName) ||
    left.skillName.localeCompare(right.skillName)
  );
}

function compareNodeLabels<TNode>(left: TNode | undefined, right: TNode | undefined): number {
  const leftLabel = extractNodeLabel(left);
  const rightLabel = extractNodeLabel(right);
  return leftLabel.localeCompare(rightLabel);
}

function extractNodeLabel(node: unknown): string {
  if (!node) {
    return '';
  }
  if (isSkill(node)) {
    return node.skill.name;
  }
  if (isTopic(node)) {
    return topicDisplayLabel(node.skillName, node.topicName);
  }
  return '';
}

function isSkill(node: unknown): node is SkillWithResourcesResponse {
  return !!node && typeof node === 'object' && 'skill' in node;
}

function isTopic(node: unknown): node is TopicNodeData {
  return !!node && typeof node === 'object' && 'topicName' in node && 'skillName' in node;
}

function resolveTopicItems(skill: SkillWithResourcesResponse): SkillTopicItemDto[] {
  const topics = skill.topics ?? [];
  return topics.filter((topic): topic is SkillTopicItemDto => typeof topic !== 'string');
}

function topicKey(skillId: string, topicName: string): string {
  return `${skillId}::${normalizeKey(topicName)}`;
}

function topicDisplayLabel(skillName: string, topicName: string): string {
  return `${skillName} / ${topicName}`;
}

function unresolvedSkillLabel(skillId: string): string {
  return skillId ? `Unknown skill (${skillId})` : 'Unknown skill';
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

function compareSortOrder(left: number | undefined, right: number | undefined): number {
  const safeLeft = left ?? Number.MAX_SAFE_INTEGER;
  const safeRight = right ?? Number.MAX_SAFE_INTEGER;
  return safeLeft - safeRight;
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function labelize(value: string): string {
  return value
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}